import React, { useRef } from 'react';
import { Sun, Moon, Upload, Shuffle } from 'lucide-react';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Separator } from '../ui/separator';
import { Card, CardContent } from '../ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { ToggleGroup, ToggleGroupItem } from '../ui/toggle-group';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../ui/accordion';
import { type ThemeConfig, type ThemeName, type TweakcnTheme } from '../../hooks/use-theme-config';

interface ThemeControlsProps {
  config: ThemeConfig;
  onChange: (update: Partial<ThemeConfig>) => void;
}

// ── Shadcn theme presets — 4 palette dots ────────────────────────────────────
const THEME_PRESETS: Array<{
  value: ThemeName;
  label: string;
  dots: [string, string, string, string];
}> = [
  { value: 'default', label: 'Default', dots: ['#18181b', '#71717a', '#d4d4d8', '#f4f4f5'] },
  { value: 'blue',    label: 'Blue',    dots: ['#2563eb', '#60a5fa', '#bfdbfe', '#dbeafe'] },
  { value: 'green',   label: 'Green',   dots: ['#16a34a', '#4ade80', '#bbf7d0', '#dcfce7'] },
  { value: 'red',     label: 'Red',     dots: ['#dc2626', '#f87171', '#fecaca', '#fee2e2'] },
  { value: 'rose',    label: 'Rose',    dots: ['#e11d48', '#fb7185', '#fda4af', '#ffe4e6'] },
  { value: 'orange',  label: 'Orange',  dots: ['#ea580c', '#fb923c', '#fdba74', '#ffedd5'] },
];

// ── Tweakcn named themes — 2 accent dots ─────────────────────────────────────
const TWEAKCN_THEMES: Array<{
  value: TweakcnTheme;
  label: string;
  dots: [string, string];
}> = [
  { value: 'modern-minimal', label: 'Modern Minimal', dots: ['#2563eb', '#e2e8f0'] },
  { value: 'violet-bloom',   label: 'Violet Bloom',   dots: ['#7c3aed', '#ec4899'] },
  { value: 't3-chat',        label: 'T3 Chat',        dots: ['#881337', '#fda4af'] },
  { value: 'twitter',        label: 'Twitter',        dots: ['#1d9bf0', '#0f172a'] },
  { value: 'mocha-mousse',   label: 'Mocha Mousse',   dots: ['#a07050', '#dab896'] },
  { value: 'bubblegum',      label: 'Bubblegum',      dots: ['#ec4899', '#fef08a'] },
];

const RADIUS_OPTIONS = ['0', '0.3', '0.5', '0.75', '1.0'];

const BRAND_COLOR_VARS: Array<{ label: string; var: string }> = [
  { label: 'Primary',   var: '--primary'   },
  { label: 'Secondary', var: '--secondary' },
  { label: 'Accent',    var: '--accent'    },
  { label: 'Muted',     var: '--muted'     },
];

// ── Reusable dot row ──────────────────────────────────────────────────────────
const ThemeDots: React.FC<{ dots: string[]; size?: 'sm' | 'md' }> = ({ dots, size = 'sm' }) => (
  <span className="flex gap-0.5">
    {dots.map((color, i) => (
      <span
        key={i}
        className={
          size === 'sm'
            ? 'inline-block h-3 w-3 rounded-full border border-black/10 dark:border-white/20 flex-shrink-0'
            : 'inline-block h-3.5 w-3.5 rounded-full border border-black/10 dark:border-white/20 flex-shrink-0'
        }
        style={{ backgroundColor: color }}
      />
    ))}
  </span>
);

/** HSL "H S% L%" → hex */
function hslToHex(hslStr: string): string {
  try {
    const parts = hslStr.trim().split(/\s+/);
    if (parts.length < 3) return '#000000';
    const h = parseFloat(parts[0]) / 360;
    const s = parseFloat(parts[1].replace('%', '')) / 100;
    const l = parseFloat(parts[2].replace('%', '')) / 100;
    const a = s * Math.min(l, 1 - l);
    const f = (n: number) => {
      const k = (n + h * 12) % 12;
      const color = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
      return Math.round(255 * color).toString(16).padStart(2, '0');
    };
    return `#${f(0)}${f(8)}${f(4)}`;
  } catch {
    return '#000000';
  }
}

function getCSSVar(varName: string): string {
  return getComputedStyle(document.documentElement).getPropertyValue(varName).trim();
}

function hexToHsl(hex: string): string {
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h = 0, s = 0;
  const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
      case g: h = ((b - r) / d + 2) / 6; break;
      case b: h = ((r - g) / d + 4) / 6; break;
    }
  }
  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

export const ThemeControls: React.FC<ThemeControlsProps> = ({ config, onChange }) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const activePreset = THEME_PRESETS.find((p) => p.value === config.theme) ?? THEME_PRESETS[0];
  const activeTweakcn = TWEAKCN_THEMES.find((t) => t.value === config.tweakcnTheme);

  const handleRandomTheme = () => {
    const others = THEME_PRESETS.filter((p) => p.value !== config.theme);
    const random = others[Math.floor(Math.random() * others.length)];
    if (random) onChange({ theme: random.value, tweakcnTheme: '' });
  };

  const handleRandomTweakcn = () => {
    const others = TWEAKCN_THEMES.filter((t) => t.value !== config.tweakcnTheme);
    const random = others[Math.floor(Math.random() * others.length)];
    if (random) onChange({ tweakcnTheme: random.value });
    else onChange({ tweakcnTheme: TWEAKCN_THEMES[0].value });
  };

  const handleImportTheme = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const json = JSON.parse(ev.target?.result as string);
        if (json && typeof json === 'object') {
          Object.entries(json).forEach(([key, value]) => {
            if (key.startsWith('--') && typeof value === 'string') {
              document.documentElement.style.setProperty(key, value);
            }
          });
        }
      } catch { /* ignore invalid JSON */ }
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  return (
    <div className="space-y-5">

      {/* ── Shadcn UI Theme Presets ─────────────────────────────────── */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <p className="text-sm font-medium text-foreground">Shadcn UI Theme Presets</p>
          <Button variant="ghost" size="sm" className="h-7 gap-1.5 text-xs" onClick={handleRandomTheme}>
            <Shuffle className="h-3 w-3" />
            Random
          </Button>
        </div>
        <Select
          value={config.theme}
          onValueChange={(v) => onChange({ theme: v as ThemeName, tweakcnTheme: '' })}
        >
          <SelectTrigger className="h-9 dark:bg-muted dark:border-muted-foreground/20">
            <SelectValue>
              <span className="flex items-center gap-2">
                <ThemeDots dots={activePreset.dots} size="sm" />
                <span className="capitalize">{activePreset.label}</span>
              </span>
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            {THEME_PRESETS.map((preset) => (
              <SelectItem key={preset.value} value={preset.value}>
                <span className="flex items-center gap-2">
                  <ThemeDots dots={preset.dots} size="sm" />
                  <span className="capitalize">{preset.label}</span>
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <Separator />

      {/* ── Tweakcn Theme Presets ───────────────────────────────────── */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <p className="text-sm font-medium text-foreground">Tweakcn Theme Presets</p>
          <Button variant="ghost" size="sm" className="h-7 gap-1.5 text-xs" onClick={handleRandomTweakcn}>
            <Shuffle className="h-3 w-3" />
            Random
          </Button>
        </div>
        <Select
          value={config.tweakcnTheme || 'none'}
          onValueChange={(v) => onChange({ tweakcnTheme: v === 'none' ? '' : (v as TweakcnTheme) })}
        >
          <SelectTrigger className="h-9 dark:bg-muted dark:border-muted-foreground/20">
            {activeTweakcn ? (
              <SelectValue>
                <span className="flex items-center gap-2">
                  <ThemeDots dots={activeTweakcn.dots} size="md" />
                  <span>{activeTweakcn.label}</span>
                </span>
              </SelectValue>
            ) : (
              <SelectValue placeholder="Choose Tweakcn Theme" />
            )}
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">
              <span className="text-muted-foreground">Choose Tweakcn Theme</span>
            </SelectItem>
            {TWEAKCN_THEMES.map((t) => (
              <SelectItem key={t.value} value={t.value}>
                <span className="flex items-center gap-2">
                  <ThemeDots dots={t.dots} size="md" />
                  {t.label}
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <Separator />

      {/* ── Radius ─────────────────────────────────────────────────── */}
      <div className="space-y-2">
        <p className="text-sm font-medium text-foreground">Radius</p>
        <ToggleGroup
          type="single"
          value={config.radius}
          onValueChange={(v) => { if (v) onChange({ radius: v }); }}
          className="flex gap-1"
        >
          {RADIUS_OPTIONS.map((r) => (
            <ToggleGroupItem
              key={r}
              value={r}
              variant="outline"
              size="sm"
              className="flex-1 h-8 text-xs data-[state=on]:border-foreground data-[state=on]:bg-foreground data-[state=on]:text-background"
            >
              {r}
            </ToggleGroupItem>
          ))}
        </ToggleGroup>
      </div>

      <Separator />

      {/* ── Mode ───────────────────────────────────────────────────── */}
      <div className="space-y-2">
        <p className="text-sm font-medium text-foreground">Mode</p>
        <ToggleGroup
          type="single"
          value={config.mode}
          onValueChange={(v) => { if (v) onChange({ mode: v as 'light' | 'dark' }); }}
          className="grid w-full grid-cols-2 gap-1"
        >
          <ToggleGroupItem
            value="light"
            variant="outline"
            className="h-9 data-[state=on]:border-foreground data-[state=on]:bg-foreground data-[state=on]:text-background"
          >
            <Sun className="mr-1.5 h-3.5 w-3.5" />
            Light
          </ToggleGroupItem>
          <ToggleGroupItem
            value="dark"
            variant="outline"
            className="h-9 data-[state=on]:border-foreground data-[state=on]:bg-foreground data-[state=on]:text-background"
          >
            <Moon className="mr-1.5 h-3.5 w-3.5" />
            Dark
          </ToggleGroupItem>
        </ToggleGroup>
      </div>

      <Separator />

      {/* ── Import Theme ───────────────────────────────────────────── */}
      <div>
        <input
          ref={fileInputRef}
          type="file"
          accept=".json"
          className="hidden"
          onChange={handleImportTheme}
        />
        <Button variant="outline" className="w-full dark:bg-muted dark:border-muted-foreground/20 dark:hover:bg-muted/70" onClick={() => fileInputRef.current?.click()}>
          <Upload className="mr-2 h-4 w-4" />
          Import Theme
        </Button>
      </div>

      <Separator />

      {/* ── Brand Colors ───────────────────────────────────────────── */}
      <Accordion type="single" collapsible className="w-full">
        <AccordionItem value="brand-colors" className="border-none">
          <AccordionTrigger className="text-sm font-medium text-foreground py-2 hover:no-underline">
            Brand Colors
          </AccordionTrigger>
          <AccordionContent>
            <div className="space-y-3 pt-1">
              {BRAND_COLOR_VARS.map(({ label, var: varName }) => (
                <div key={varName} className="flex items-center justify-between">
                  <Label className="text-sm text-foreground">{label}</Label>
                  <input
                    type="color"
                    defaultValue={hslToHex(getCSSVar(varName))}
                    onChange={(e) => {
                      document.documentElement.style.setProperty(varName, hexToHsl(e.target.value));
                    }}
                    className="h-8 w-12 cursor-pointer rounded border border-input bg-background dark:bg-muted dark:border-muted-foreground/20 p-0.5"
                  />
                </div>
              ))}
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>

      <Separator />

      {/* ── Advanced Customization ─────────────────────────────────── */}
      <Card className="bg-muted/40 border-muted dark:bg-muted/20 dark:border-muted-foreground/15">
        <CardContent className="pt-4 pb-4 space-y-3">
          <div className="flex items-start gap-2">
            <span className="text-base">🎨</span>
            <div>
              <p className="text-sm font-medium">Advanced Customization</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                For advanced theme customization with real-time preview, visual color picker, and
                hundreds of prebuilt themes, visit tweakcn.com.
              </p>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            className="w-full dark:bg-muted/60 dark:border-muted-foreground/20 dark:hover:bg-muted"
            onClick={() => window.open('https://tweakcn.com', '_blank', 'noopener,noreferrer')}
          >
            Open Tweakcn ↗
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};
